import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Grid, Chip } from "@mui/material";
import {
  Person,
  Phone,
  Email,
  School,
  Assignment,
  CalendarToday,
  SupervisorAccount,
  Star,
  TrendingUp,
  Psychology,
  MenuBook,
  Wc,
  LocationOn,
  LocationCity,
  Public,
} from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import Table from "examples/Tables/Table";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import axios from "utils/api";
import { baseURL } from "utils/api";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { toast } from "react-toastify";
import { getEHCById } from "services/ehcService";

const ViewEHC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ehcData, setEhcData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [studentTableRows, setStudentTableRows] = useState([]);

  useEffect(() => {
    const fetchEHCData = async () => {
      setLoading(true);
      try {
        const response = await getEHCById(id);
        if (response.success && response.data) {
          setEhcData(response.data);

          // Fetch students for this parent
          if (response.data.parentid?._id) {
            try {
              const studentsResponse = await axios.get(
                `${baseURL}/student?parentId=${response.data.parentid._id}`
              );
              if (studentsResponse.data.success && studentsResponse.data.data.length > 0) {
                setStudents(studentsResponse.data.data);
                // Convert API data to table rows format
                const rows = studentsResponse.data.data.map((student, index) => ({
                  sno: (
                    <ArgonTypography variant="caption" color="text" fontWeight="medium">
                      {index + 1}
                    </ArgonTypography>
                  ),
                  "student Name": (
                    <ArgonTypography variant="caption" color="text" fontWeight="medium">
                      {student.name || student.fullName || "N/A"}
                    </ArgonTypography>
                  ),
                  Gender: (
                    <ArgonTypography variant="caption" color="text" fontWeight="medium">
                      {student.gender || "N/A"}
                    </ArgonTypography>
                  ),
                  "Date of Birth": (
                    <ArgonTypography variant="caption" color="text" fontWeight="medium">
                      {student.dob ? new Date(student.dob).toLocaleDateString() : "N/A"}
                    </ArgonTypography>
                  ),
                  "School/College/University Name": (
                    <ArgonTypography variant="caption" color="text" fontWeight="medium">
                      {student.schoolName || student.institution || "N/A"}
                    </ArgonTypography>
                  ),
                  "Preferred Time Duration": (
                    <ArgonTypography variant="caption" color="text" fontWeight="medium">
                      {student.preferredTimeDuration || "N/A"} mins
                    </ArgonTypography>
                  ),
                  "Tuition Tenure": (
                    <ArgonTypography variant="caption" color="text" fontWeight="medium">
                      {student.noOfClassesPerWeek || "N/A"} classes/week
                    </ArgonTypography>
                  ),
                  "mode Of Tuition": (
                    <ArgonTypography variant="caption" color="text" fontWeight="medium">
                      {student.modeOfTuition || "N/A"}
                    </ArgonTypography>
                  ),
                }));
                setStudentTableRows(rows);
              }
            } catch (studentsError) {
              console.error("Error fetching students:", studentsError);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching EHC:", error);
        toast.error("Failed to load EHC data");
        navigate("/activities/ehc");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEHCData();
    }
  }, [id, navigate]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3}>
          <ArgonTypography variant="h5" textAlign="center">
            Loading EHC Data...
          </ArgonTypography>
        </ArgonBox>
      </DashboardLayout>
    );
  }

  if (!ehcData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3}>
          <ArgonTypography variant="h5" textAlign="center" color="error">
            EHC Data Not Found
          </ArgonTypography>
        </ArgonBox>
      </DashboardLayout>
    );
  }

  const parent = ehcData?.parentid || {};
  const shortTermGoals =
    Array.isArray(ehcData?.shortTermGoals) && ehcData.shortTermGoals.length > 0
      ? ehcData.shortTermGoals
      : ehcData?.srcConsultation?.shortTermGoal
      ? [ehcData.srcConsultation.shortTermGoal]
      : [];
  const longTermGoals =
    Array.isArray(ehcData?.longTermGoals) && ehcData.longTermGoals.length > 0
      ? ehcData.longTermGoals
      : ehcData?.srcConsultation?.longTermGoal
      ? [ehcData.srcConsultation.longTermGoal]
      : [];

  const toTitleCase = (value) => {
    if (!value || typeof value !== "string") {
      return "N/A";
    }

    return value
      .replace(/[_-]+/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  };

  const displayValue = (value, fallback) => {
    if (value === null || value === undefined || value === "") {
      return fallback ?? "N/A";
    }

    if (Array.isArray(value)) {
      return value.map((item) => displayValue(item)).join(", ");
    }

    if (typeof value === "object") {
      if (value.coordinates && Array.isArray(value.coordinates)) {
        return value.coordinates.join(", ");
      }
      if (value.name) {
        return displayValue(value.name);
      }
      return fallback ?? "N/A";
    }

    return value;
  };

  const genderValue = toTitleCase(parent.gender);
  const tuitionModeValue = toTitleCase(parent.modeoftution);
  const locationValue = displayValue(parent.address, displayValue(parent.location));
  const stateValue = displayValue(parent.state, displayValue(parent.location));
  const cityValue = displayValue(parent.city);

  const parentInfoItems = [
    {
      label: "Parent Name",
      value: displayValue(parent.fullName),
      icon: Person,
      iconColor: "#4C51BF",
      iconBg: "rgba(76, 81, 191, 0.12)",
      grid: { xs: 12, sm: 6, md: 3 },
    },
    {
      label: "Mobile",
      value: displayValue(parent.phone),
      icon: Phone,
      iconColor: "#1BC5BD",
      iconBg: "rgba(27, 197, 189, 0.12)",
      grid: { xs: 12, sm: 6, md: 3 },
    },
    // {
    //   label: "Gender",
    //   value: genderValue,
    //   icon: Wc,
    //   iconColor: "#4299E1",
    //   iconBg: "rgba(66, 153, 225, 0.14)",
    //   chipBg: "rgba(191, 219, 254, 0.9)",
    //   chipColor: "#1D4ED8",
    // },
    {
      label: "Email",
      value: displayValue(parent.email),
      icon: Email,
      iconColor: "#38A169",
      iconBg: "rgba(56, 161, 105, 0.12)",
      grid: { xs: 12, sm: 6, md: 3 },
    },
    {
      label: "City",
      value: cityValue,
      icon: LocationCity,
      iconColor: "#EC4899",
      iconBg: "rgba(236, 72, 153, 0.12)",
      grid: { xs: 12, sm: 6, md: 3 },
    },
    {
      label: "Location",
      value: locationValue,
      icon: LocationOn,
      iconColor: "#F59E0B",
      iconBg: "rgba(245, 158, 11, 0.12)",
      grid: { xs: 12, sm: 12, md: 6 },
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <ArgonTypography variant="h4" fontWeight="bold">
            View EHC Details
          </ArgonTypography>
          <ArgonBox display="flex" gap={2}>
            <ArgonButton
              variant="gradient"
              color="info"
              onClick={() => navigate(`/activities/ehc/edit/${id}`)}
            >
              Edit
            </ArgonButton>
            <ArgonButton variant="gradient" color="info" onClick={() => navigate("/ehc")}>
              Back to List
            </ArgonButton>
          </ArgonBox>
        </ArgonBox>

        {/* Parent Information Section */}
        <Card
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            borderRadius: "16px",
            background: "linear-gradient(135deg, #f8f9ff 0%, #ffffff 75%)",
            border: "1px solid #e5e7ff",
            boxShadow: "0 10px 24px rgba(80, 102, 255, 0.08)",
          }}
        >
          <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
            <ArgonBox display="flex" alignItems="center" gap={2}>
              <ArgonBox
                sx={{
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  borderRadius: "14px",
                  p: 1.2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 10px 20px rgba(102, 126, 234, 0.3)",
                }}
              >
                <Person sx={{ color: "#ffffff", fontSize: 24 }} />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h5" fontWeight="medium">
                  Parent Information
                </ArgonTypography>
                <ArgonTypography variant="body2" sx={{ color: "text.secondary" }}>
                  Complete parent and tuition details
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
          </ArgonBox>

          <Grid container spacing={2} alignItems="stretch">
            {parentInfoItems.map((item) => {
              const IconComponent = item.icon;
              const valueToShow = item.value || "N/A";
              const showChip = item.chipBg && valueToShow !== "N/A";

              return (
                <Grid item {...item.grid} key={item.label}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "#ffffff",
                      borderRadius: "14px",
                      p: { xs: 1.75, md: 2 },
                      boxShadow: "0 6px 16px rgba(15, 23, 42, 0.06)",
                      border: "1px solid rgba(226, 232, 240, 0.7)",
                      minHeight: 122,
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 24px rgba(15, 23, 42, 0.1)",
                      },
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.2} mb={1.2}>
                      <ArgonBox
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: item.iconBg,
                        }}
                      >
                        <IconComponent sx={{ color: item.iconColor, fontSize: 18 }} />
                      </ArgonBox>
                      <ArgonTypography
                        variant="button"
                        fontWeight="bold"
                        sx={{ textTransform: "none" }}
                      >
                        {item.label}
                      </ArgonTypography>
                    </ArgonBox>

                    {showChip ? (
                      <Chip
                        label={valueToShow}
                        size="small"
                        sx={{
                          backgroundColor: item.chipBg,
                          color: item.chipColor,
                          fontWeight: 600,
                          textTransform: "capitalize",
                          px: 1.2,
                          py: 0.4,
                          borderRadius: "999px",
                          width: "fit-content",
                        }}
                      />
                    ) : (
                      <ArgonTypography
                        variant="body2"
                        color="text"
                        sx={{
                          lineHeight: 1.6,
                          wordBreak: "break-word",
                          color: "#516078",
                        }}
                      >
                        {valueToShow}
                      </ArgonTypography>
                    )}
                  </ArgonBox>
                </Grid>
              );
            })}
          </Grid>
        </Card>

        {/* Student Information Section
        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <School sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              Student Information
            </ArgonTypography>
          </ArgonBox>
          <Divider sx={{ marginBottom: 3 }} />

          <Table
            columns={[
              { name: "field", align: "left" },
              { name: "value", align: "left" },
            ]}
            rows={[
              {
                field: (
                  <ArgonTypography variant="body2" fontWeight="bold">
                    Student Name
                  </ArgonTypography>
                ),
                value: (
                  <ArgonTypography variant="body2">
                    {ehcData?.studentId?.fullName || "N/A"}
                  </ArgonTypography>
                ),
              },
              {
                field: (
                  <ArgonTypography variant="body2" fontWeight="bold">
                    Email
                  </ArgonTypography>
                ),
                value: (
                  <ArgonTypography variant="body2">
                    {ehcData?.studentId?.email || "N/A"}
                  </ArgonTypography>
                ),
              },
              {
                field: (
                  <ArgonTypography variant="body2" fontWeight="bold">
                    Phone
                  </ArgonTypography>
                ),
                value: (
                  <ArgonTypography variant="body2">
                    {ehcData?.studentId?.phone || "N/A"}
                  </ArgonTypography>
                ),
              },
              {
                field: (
                  <ArgonTypography variant="body2" fontWeight="bold">
                    Class
                  </ArgonTypography>
                ),
                value: (
                  <ArgonTypography variant="body2">
                    {ehcData?.studentId?.class?.name || "N/A"}
                  </ArgonTypography>
                ),
              },
              {
                field: (
                  <ArgonTypography variant="body2" fontWeight="bold">
                    Board
                  </ArgonTypography>
                ),
                value: (
                  <ArgonTypography variant="body2">
                    {ehcData?.studentId?.board?.name || "N/A"}
                  </ArgonTypography>
                ),
              },
            ]}
          />
        </Card> */}

        {/* Student List Section */}
        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <School sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              Student List
            </ArgonTypography>
          </ArgonBox>

          {studentTableRows.length > 0 ? (
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table
                columns={[
                  { name: "sno", align: "center" },
                  { name: "student Name", align: "left" },
                  { name: "Gender", align: "left" },
                  { name: "Date of Birth", align: "left" },
                  { name: "School/College/University Name", align: "left" },
                  { name: "Preferred Time Duration", align: "left" },
                  { name: "Tuition Tenure", align: "left" },
                  { name: "mode Of Tuition", align: "center" },
                ]}
                rows={studentTableRows}
              />
            </ArgonBox>
          ) : (
            <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
              <ArgonTypography variant="body2" color="text">
                No students found for this parent
              </ArgonTypography>
            </ArgonBox>
          )}
        </Card>

        {/* Tutor Information Section */}
        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <SupervisorAccount sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              Tutor Information
            </ArgonTypography>
          </ArgonBox>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Tutor Name
              </ArgonTypography>
              <ArgonInput value={ehcData?.tutorId?.fullName || "N/A"} disabled fullWidth />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Tutor Phone Number
              </ArgonTypography>
              <ArgonInput value={ehcData?.tutorId?.phone || "N/A"} disabled fullWidth />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Tutor Email
              </ArgonTypography>
              <ArgonInput value={ehcData?.tutorId?.email || "N/A"} disabled fullWidth />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Address
              </ArgonTypography>
              <ArgonInput value={ehcData?.tutorId?.address || "N/A"} disabled fullWidth />
            </Grid>
          </Grid>
        </Card>

        {/* Discovery Call Section */}
        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <Phone sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              Discovery Call
            </ArgonTypography>
          </ArgonBox>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Call Done By (Admin Name)
              </ArgonTypography>
              <ArgonInput
                value={ehcData?.discoverCall?.callDoneBy?.fullName || "N/A"}
                disabled
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Call Date
              </ArgonTypography>
              <ArgonInput
                value={
                  ehcData?.discoverCall?.callDate
                    ? new Date(ehcData.discoverCall.callDate).toLocaleDateString()
                    : "N/A"
                }
                disabled
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Call Status
              </ArgonTypography>
              <ArgonInput value={ehcData?.discoverCall?.callStatus || "N/A"} disabled fullWidth />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Remarks
              </ArgonTypography>
              <ArgonInput
                value={ehcData?.discoverCall?.remarks || "N/A"}
                disabled
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Card>

        {/* EHC Coordination Section */}
        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <Assignment sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              EHC Coordination
            </ArgonTypography>
          </ArgonBox>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Supervisor (Admin Name)
              </ArgonTypography>
              <ArgonInput
                value={
                  ehcData?.ehcCoordination?.supervisorName ||
                  ehcData?.ehcCoordination?.supervisorId?.fullName ||
                  "N/A"
                }
                disabled
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                EHC Name
              </ArgonTypography>
              <ArgonInput value={ehcData?.ehcCoordination?.ehcName || "N/A"} disabled fullWidth />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Time Slot
              </ArgonTypography>
              <ArgonInput value={ehcData?.ehcCoordination?.timeSlot || "N/A"} disabled fullWidth />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Remarks
              </ArgonTypography>
              <ArgonInput
                value={ehcData?.ehcCoordination?.remarks || "N/A"}
                disabled
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Card>

        {/* Content Knowledge Section */}
        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <MenuBook sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              Content Knowledge
            </ArgonTypography>
          </ArgonBox>

          {ehcData?.contentKnowledge &&
          Array.isArray(ehcData.contentKnowledge) &&
          ehcData.contentKnowledge.length > 0 ? (
            ehcData.contentKnowledge.map((subject, index) => (
              <ArgonBox key={index} mb={3}>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Subject {index + 1}
                </ArgonTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="caption" color="text" fontWeight="bold">
                      Subject Name
                    </ArgonTypography>
                    <ArgonInput value={subject?.name || "N/A"} disabled fullWidth />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="caption" color="text" fontWeight="bold">
                      Understanding Level (0-10)
                    </ArgonTypography>
                    <ArgonInput value={subject?.understandingLevel ?? "N/A"} disabled fullWidth />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="caption" color="text" fontWeight="bold">
                      Remarks
                    </ArgonTypography>
                    <ArgonInput value={subject?.remarks || "N/A"} disabled fullWidth />
                  </Grid>
                </Grid>
              </ArgonBox>
            ))
          ) : (
            <ArgonBox p={2} textAlign="center">
              <ArgonTypography variant="body2" color="text">
                No subjects added yet
              </ArgonTypography>
            </ArgonBox>
          )}
        </Card>

        {/* Core Parameters Section */}
        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <Star sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              Core Parameters
            </ArgonTypography>
          </ArgonBox>

          <Grid container spacing={3}>
            {[
              { key: "overallFoundation", label: "Overall Foundation" },
              { key: "memoryRetention", label: "Memory Retention" },
              { key: "confidenceInExplaining", label: "Confidence in Explaining" },
              { key: "writingSkills", label: "Writing Skills" },
              { key: "timeManagement", label: "Time Management" },
            ].map((param) => (
              <Grid item xs={12} md={6} key={param.key}>
                <ArgonTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  {param.label}
                </ArgonTypography>
                <ArgonBox mt={1}>
                  <ArgonTypography variant="body2" fontWeight="bold">
                    Rating: {ehcData?.coreParameters?.[param.key]?.rating || "N/A"}
                  </ArgonTypography>
                  <ArgonTypography variant="body2">
                    Remarks: {ehcData?.coreParameters?.[param.key]?.remarks || "N/A"}
                  </ArgonTypography>
                </ArgonBox>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* Behavioural Parameters Section */}
        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <Psychology sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              Behavioural Parameters
            </ArgonTypography>
          </ArgonBox>

          <Grid container spacing={3}>
            {[
              { key: "regularInPractice", label: "Regular in Practice" },
              { key: "asksDoubtsActively", label: "Asks Doubts Actively" },
              { key: "followsStudyPlan", label: "Follows Study Plan" },
            ].map((param) => (
              <Grid item xs={12} md={6} key={param.key}>
                <ArgonTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  {param.label}
                </ArgonTypography>
                <ArgonInput
                  value={ehcData?.behaviouralParameters?.[param.key] || "N/A"}
                  disabled
                  fullWidth
                />
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* Parents Review Section */}
        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <Email sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              Parents Review
            </ArgonTypography>
          </ArgonBox>

          <Grid container spacing={3}>
            {[
              { key: "regularityOfEducator", label: "Regularity of Educator" },
              { key: "studentSelfStudyTime", label: "Student Self Study Time" },
              { key: "inTouchWithTutor", label: "In Touch with Tutor" },
              { key: "concernsRaisedByParents", label: "Concerns Raised by Parents" },
            ].map((param) => (
              <Grid item xs={12} md={6} key={param.key}>
                <ArgonTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  {param.label}
                </ArgonTypography>
                <ArgonInput
                  value={ehcData?.parentsReview?.[param.key] || "N/A"}
                  disabled
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* SWOT Assessment Section */}
        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <TrendingUp sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              SWOT Assessment
            </ArgonTypography>
          </ArgonBox>

          <Grid container spacing={3}>
            {["strength", "weakness", "opportunity", "threats"].map((item) => (
              <Grid item xs={12} md={6} key={item}>
                <ArgonTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  {item.charAt(0).toUpperCase() + item.slice(1)}
                </ArgonTypography>
                <ArgonInput
                  value={ehcData?.swotAssessment?.[item] || "N/A"}
                  disabled
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* Final Recommendation Section */}
        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <CalendarToday sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              Final Recommendation & Status
            </ArgonTypography>
          </ArgonBox>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                EHC Status
              </ArgonTypography>
              <ArgonInput value={ehcData?.status || "N/A"} disabled fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Created At
              </ArgonTypography>
              <ArgonInput
                value={
                  ehcData?.createdAt ? new Date(ehcData.createdAt).toLocaleDateString() : "N/A"
                }
                disabled
                fullWidth
              />
            </Grid>
          </Grid>
        </Card>

        {/* SRC Section */}
        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <Assignment sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              SRC (Strategic Roadmapping And Consultation)
            </ArgonTypography>
          </ArgonBox>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Date of Meeting
              </ArgonTypography>
              <ArgonInput
                value={
                  ehcData?.srcConsultation?.meetingDate
                    ? new Date(ehcData.srcConsultation.meetingDate).toLocaleDateString()
                    : "N/A"
                }
                disabled
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Time of Meeting
              </ArgonTypography>
              <ArgonInput
                value={ehcData?.srcConsultation?.meetingTime || "N/A"}
                disabled
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Meeting Link
              </ArgonTypography>
              <ArgonInput
                value={ehcData?.srcConsultation?.meetingLink || "N/A"}
                disabled
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Academic Coach
              </ArgonTypography>
              <ArgonInput
                value={ehcData?.srcConsultation?.academicCoach || "N/A"}
                disabled
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Academic Coach Image
              </ArgonTypography>
              {ehcData?.srcConsultation?.academicCoachImage ? (
                <ArgonBox mt={1}>
                  <img
                    src={ehcData.srcConsultation.academicCoachImage}
                    alt="Academic Coach"
                    height="80px"
                    width="140px"
                    style={{
                      border: "1px solid #e0e7ff",
                      borderRadius: "8px",
                      objectFit: "cover",
                    }}
                  />
                </ArgonBox>
              ) : (
                <ArgonInput value="N/A" disabled fullWidth />
              )}
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <Assignment sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              Short-term Goal (20-25 days)
            </ArgonTypography>
          </ArgonBox>

          {shortTermGoals.length > 0 ? (
            shortTermGoals.map((point, index) => (
              <ArgonBox key={`view-short-${index}`} mb={1.5}>
                <ArgonTypography variant="body2" color="text" fontWeight="regular">
                  {index + 1}. {point}
                </ArgonTypography>
              </ArgonBox>
            ))
          ) : (
            <ArgonTypography variant="body2" color="text">
              N/A
            </ArgonTypography>
          )}
        </Card>

        <Card sx={{ padding: 3, marginBottom: 3 }}>
          <ArgonBox mb={2} display="flex" alignItems="center" gap={1}>
            <Assignment sx={{ fontSize: 28, color: "#1976d2" }} />
            <ArgonTypography variant="h5" fontWeight="bold">
              Long-term Goal (20-25 days)
            </ArgonTypography>
          </ArgonBox>

          {longTermGoals.length > 0 ? (
            longTermGoals.map((point, index) => (
              <ArgonBox key={`view-long-${index}`} mb={1.5}>
                <ArgonTypography variant="body2" color="text" fontWeight="regular">
                  {index + 1}. {point}
                </ArgonTypography>
              </ArgonBox>
            ))
          ) : (
            <ArgonTypography variant="body2" color="text">
              N/A
            </ArgonTypography>
          )}
        </Card>
      </ArgonBox>
    </DashboardLayout>
  );
};

export default ViewEHC;
